import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { DollarSign, Heart, Clock, Truck, Shield, Users, ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"
import { useLanguage } from "@/lib/i18n"

export default function DriversSection() {
  const { t } = useLanguage()

  const benefits = [
    {
      icon: DollarSign,
      titleKey: "drivers.pay.title",
      descKey: "drivers.pay.desc",
    },
    {
      icon: Heart,
      titleKey: "drivers.benefits.title",
      descKey: "drivers.benefits.desc",
    },
    {
      icon: Clock,
      titleKey: "drivers.home.title",
      descKey: "drivers.home.desc",
    },
    {
      icon: Truck,
      titleKey: "drivers.equipment.title",
      descKey: "drivers.equipment.desc",
    },
    {
      icon: Shield,
      titleKey: "drivers.security.title",
      descKey: "drivers.security.desc",
    },
    {
      icon: Users,
      titleKey: "drivers.support.title",
      descKey: "drivers.support.desc",
    },
  ]

  const requirements = ["drivers.req1", "drivers.req2", "drivers.req3", "drivers.req4", "drivers.req5", "drivers.req6"]

  return (
    <section id="drivers" className="py-20 bg-secondary">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-primary font-semibold tracking-wider uppercase mb-4">{t("drivers.tagline")}</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-balance">{t("drivers.title")}</h2>
          <p className="text-muted-foreground text-lg leading-relaxed">{t("drivers.description")}</p>
        </div>

        {/* Benefits grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {benefits.map((benefit) => (
            <Card key={benefit.titleKey} className="bg-card border-border hover:border-primary/50 transition-all">
              <CardHeader>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-2">
                  <benefit.icon className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg text-card-foreground">{t(benefit.titleKey)}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(benefit.descKey)}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Requirements section */}
        <div className="bg-card border border-border rounded-lg p-8 max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-card-foreground">{t("drivers.requirements")}</h3>
              <p className="text-muted-foreground mb-6">{t("drivers.requirements.desc")}</p>
              <ul className="space-y-3">
                {requirements.map((reqKey) => (
                  <li key={reqKey} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 flex-shrink-0" />
                    <span className="text-muted-foreground">{t(reqKey)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center md:text-left">
              <div className="bg-primary/10 rounded-lg p-6 border border-primary/30">
                <p className="text-4xl font-bold text-primary mb-2">$0.55-$0.75</p>
                <p className="text-muted-foreground mb-4">{t("drivers.perMile")}</p>
                <p className="text-sm text-muted-foreground mb-6">{t("drivers.bonuses")}</p>
                <Button size="lg" asChild className="w-full group">
                  <Link to="#apply">
                    {t("drivers.applyNow")}
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
